import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { listAudits, saveAudit, deleteAudit } from "@/lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Audits() {
  const [audits, setAudits] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const loadAudits = async () => {
    try {
      const list = await listAudits();
      setAudits(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load audits.");
      setAudits([]);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const saveId = params.get("save");

    (async () => {
      if (saveId) {
        try {
          await saveAudit(saveId);
          toast.success("Audit saved.");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not save audit.");
        }
        window.history.replaceState({}, "", "/app/audits");
      }
      await loadAudits();
    })();
  }, [location.search]);

  const onDelete = async (auditId) => {
    setDeletingId(auditId);
    try {
      await deleteAudit(auditId);
      try {
        sessionStorage.removeItem(`audit_${auditId}`);
      } catch {}
      toast.success("Audit deleted.");
      setAudits((prev) => (prev || []).filter((a) => a.audit_id !== auditId));
      setConfirmingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete this audit.");
    } finally {
      setDeletingId(null);
    }
  };

  if (audits === null) {
    return <div className="overline">Loading…</div>;
  }

  return (
    <div data-testid="audits-page" className="space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Saved audits</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Your LinkedIn over time.
          </h1>
          <p className="mt-3 text-[var(--ink-2)] max-w-xl">
            Re-run an audit whenever your positioning shifts. Track your score over months, not weeks.
          </p>
        </div>
        <Button
          onClick={() => navigate("/")}
          className="btn-accent rounded-full h-11 px-5"
          data-testid="audits-new-btn"
        >
          New audit →
        </Button>
      </div>

      {audits.length === 0 ? (
        <div className="card-tech rounded-sm p-10 text-center">
          <div className="overline mb-2">No audits yet</div>
          <p className="text-[var(--ink-2)]">Run your first LinkedIn audit on the home page.</p>
          <Button
            onClick={() => navigate("/")}
            className="btn-primary rounded-full h-10 px-5 mt-5"
          >
            Start
          </Button>
        </div>
      ) : (
        <ul data-testid="audit-list" className="card-tech rounded-sm divide-y divide-[var(--line)]">
          {audits.map((a) => {
            const confirming = confirmingId === a.audit_id;
            const deleting = deletingId === a.audit_id;
            return (
              <li
                key={a.audit_id}
                className="grid grid-cols-12 gap-4 p-5 items-center hover:bg-[var(--bg-2)]"
                data-testid={`audit-item-${a.audit_id}`}
              >
                <div
                  className="col-span-2 font-display text-3xl cursor-pointer"
                  onClick={() => navigate(`/results/${a.audit_id}`)}
                >
                  {a.overall_score}
                  <span className="text-xs text-[var(--muted)] font-mono-mark ml-1">/100</span>
                </div>
                <div
                  className="col-span-6 cursor-pointer"
                  onClick={() => navigate(`/results/${a.audit_id}`)}
                >
                  <div className="font-display text-lg tracking-tight">
                    {a.founder_name || a.email || "Audit"}
                  </div>
                  <div className="text-xs text-[var(--muted)] truncate">{a.linkedin_url || "—"}</div>
                </div>
                <div className="col-span-2 text-right text-xs text-[var(--muted)] font-mono-mark">
                  {(a.created_at || "").slice(0, 10)}
                </div>
                <div className="col-span-2 flex justify-end">
                  {confirming ? (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onDelete(a.audit_id)}
                        disabled={deleting}
                        data-testid={`audit-delete-confirm-${a.audit_id}`}
                        className="text-xs font-mono-mark uppercase tracking-wider px-3 py-2 rounded-sm bg-[var(--accent)] text-white"
                      >
                        {deleting ? "…" : "Confirm"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingId(null)}
                        disabled={deleting}
                        data-testid={`audit-delete-cancel-${a.audit_id}`}
                        className="text-xs font-mono-mark uppercase tracking-wider px-3 py-2 rounded-sm border border-[var(--line)]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingId(a.audit_id)}
                      data-testid={`audit-delete-${a.audit_id}`}
                      className="text-[var(--muted)] hover:text-[var(--accent)] p-2"
                      title="Delete this audit"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
